import apiService from "./apiService";
import NangoService from "./nangoService";

export interface UserIntegration {
  id: string;
  provider: string;
  integration_status: "active" | "inactive" | "error";
  created_at: string;
  updated_at: string;
  settings?: any;
}

export interface IntegrationTestResult {
  provider: string;
  status: "success" | "error";
  response_time_ms: number;
  data_keys?: string[];
  last_sync?: string;
  has_data: boolean;
  error?: string;
  data_summary?: any;
  sample_data?: any;
}

export interface IntegrationHealthResult {
  provider: string;
  status: "healthy" | "unhealthy";
  response_time_ms: number;
  last_sync?: string;
  data_available: boolean;
  error?: string;
}

class IntegrationsService {
  /**
   * Get all integrations for the current user.
   * @param sync When true, proactively checks Nango for connections that may
   *             have been missed by webhooks. This is expensive (~27 API calls)
   *             and should only be used when actively connecting integrations.
   */
  async getUserIntegrations(sync = false): Promise<UserIntegration[]> {
    try {
      const params = sync ? "?sync=true" : "";
      const response = await apiService.get(`/oauth/integrations${params}`);
      return response.data.integrations || [];
    } catch (error) {
      console.error("Failed to get user integrations:", error);
      throw error;
    }
  }

  /**
   * Targeted single-provider sync from Nango to DB.
   * Called immediately after Nango Connect success to avoid slow polling.
   */
  async syncProviderConnection(
    provider: string,
    connectionId?: string,
  ): Promise<UserIntegration | null> {
    const response = await apiService.post(`/oauth/${provider}/sync`, {
      connectionId,
    });
    return response.data.integration || null;
  }

  /**
   * Initiate OAuth connection for an integration
   */
  async connectIntegration(
    provider: string,
    options?: {
      onSuccess?: (connectionId?: string) => void;
      onError?: (error: Error) => void;
      onClose?: () => void;
      settings?: any;
      forceReconnect?: boolean;
    },
  ): Promise<void> {
    try {
      // For OAuth integrations, we need to redirect to the OAuth flow
      const oauthProviders = this.getOAuthProviders();

      if (oauthProviders.includes(provider)) {
        // GitLab: Self-hosted instance URL can be configured via settings if needed
        // Default behavior uses gitlab.com

        // Get OAuth URL and check if it uses Nango
        const params: any = {
          ...options?.settings,
        };
        // Only pass forceReconnect if explicitly set to true
        if (options?.forceReconnect === true) {
          params.forceReconnect = "true";
        }
        const response = await apiService.get(`/oauth/${provider}/url`, {
          params,
        });

        // Check if provider uses Nango
        if (response.data.useNango) {
          console.log(`🔐 Using Nango for ${provider}`);

          // Verify we have a session token
          if (!response.data.sessionToken) {
            throw new Error("No Nango session token received from backend");
          }

          console.log(
            `📊 Session token received:`,
            response.data.sessionToken.substring(0, 30) + "...",
          );

          try {
            // Import Nango SDK dynamically to avoid bundling issues
            console.log(`📦 Importing Nango SDK...`);
            const NangoModule = await import("@nangohq/frontend");
            const Nango = NangoModule.default;
            console.log(`✅ Nango SDK imported successfully`);

            // Initialize Nango SDK directly with the session token from backend
            // This avoids creating a SECOND session which causes connectionId mismatches
            console.log(
              `✅ Initializing Nango with session token from backend`,
            );
            const nango = new Nango({
              connectSessionToken: response.data.sessionToken,
            });
            console.log(`✅ Nango instance created`);

            // Track if we already handled a successful connection
            let connectionHandled = false;

            // Open Nango Connect UI with event handlers
            console.log(`📱 Opening Nango Connect UI for ${provider}...`);
            const result = nango.openConnectUI({
              onEvent: async (event: { type: string; [key: string]: any }) => {
                console.log("📡 Nango event:", event);

                if (event.type === "connect") {
                  console.log("✅ Connection successful:", event);
                  connectionHandled = true;
                  const connectionId =
                    (event as any).connectionId || (event as any).connection_id;

                  // Immediately sync this provider's connection to the DB
                  try {
                    await this.syncProviderConnection(provider, connectionId);
                  } catch (e) {
                    console.warn(
                      "Sync after connect failed, proceeding anyway:",
                      e,
                    );
                  }
                  options?.onSuccess?.(connectionId);
                } else if (event.type === "close") {
                  console.log("ℹ️ Connection modal closed");

                  // If we didn't receive a connect event, check if connection exists
                  // Nango sometimes fails to send the connect event after OAuth completes
                  if (!connectionHandled) {
                    console.log(
                      "🔍 Checking if connection was created (connect event may have been missed)...",
                    );
                    try {
                      const syncResult =
                        await this.syncProviderConnection(provider);
                      if (
                        syncResult &&
                        syncResult.integration_status === "active"
                      ) {
                        console.log(
                          "✅ Connection found after modal close! Triggering success.",
                        );
                        connectionHandled = true;
                        options?.onSuccess?.();
                        return;
                      }
                    } catch (checkError) {
                      console.log(
                        "ℹ️ Connection check failed or no connection found:",
                        checkError,
                      );
                    }
                  }

                  if (!connectionHandled) {
                    options?.onClose?.();
                  }
                } else if (event.type === "error") {
                  console.error("❌ Connection error:", event);
                  const errorMessage =
                    (event as any).error ||
                    (event as any).message ||
                    "Connection failed";
                  options?.onError?.(new Error(errorMessage));
                }
              },
            });
            console.log(`✅ Nango Connect UI opened successfully`, result);
          } catch (nangoError) {
            console.error("❌ Error with Nango SDK:", nangoError);
            throw nangoError;
          }
        } else if (response.data.url) {
          // Legacy OAuth flow
          console.log(`🔐 Using legacy OAuth for ${provider}`);

          // Store any additional settings in sessionStorage for after OAuth redirect
          if (options?.settings) {
            sessionStorage.setItem(
              `integration_settings_${provider}`,
              JSON.stringify(options.settings),
            );
          }

          // For GitHub, Stripe, and Close, use popup instead of redirect to keep user in same window
          if (["github", "stripe", "close"].includes(provider.toLowerCase())) {
            console.log(`🔐 Using popup OAuth for ${provider}`);

            // Request OAuth URL WITH popup parameter so backend includes it in callback URL
            const popupResponse = await apiService.get(
              `/oauth/${provider}/url`,
              {
                params: {
                  ...options?.settings,
                  popup: "true", // Backend will include this in callback URL
                },
              },
            );

            if (!popupResponse.data.url) {
              throw new Error("Failed to get OAuth URL");
            }

            // Add popup parameter to the OAuth URL for the callback to detect
            const oauthUrl = new URL(popupResponse.data.url);
            oauthUrl.searchParams.set("popup", "true");
            const oauthUrlWithPopup = oauthUrl.toString();

            // Open popup window
            const width = 600;
            const height = 750;
            const left = Math.max(
              0,
              Math.floor(window.screenX + (window.outerWidth - width) / 2),
            );
            const top = Math.max(
              0,
              Math.floor(window.screenY + (window.outerHeight - height) / 2),
            );
            const popupFeatures = `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${width},height=${height},top=${top},left=${left}`;

            const popup = window.open(
              oauthUrlWithPopup,
              `oauth_${provider}`,
              popupFeatures,
            );
            if (!popup) {
              // Fallback: navigate current window (last resort)
              window.location.href = oauthUrlWithPopup;
              return;
            }

            // Track if OAuth completed successfully
            let oauthCompleted = false;
            let handled = false;
            let timeoutId: number | null = null;
            let checkClosed: number | null = null;
            let pollingPromise: Promise<void> | null = null;

            // For Close, start API polling immediately (window.opener breaks on OAuth redirect)
            if (provider.toLowerCase() === "close") {
              console.log(
                `🔄 Starting API polling for ${provider} (window.opener not reliable)`,
              );
              pollingPromise = this.startCloseConnectionPolling(provider)
                .then(() => {
                  console.log(
                    `✅ Polling detected ${provider} connection - triggering onSuccess`,
                  );
                  oauthCompleted = true;
                  handled = true;
                  popup?.close();
                  window.removeEventListener("message", messageListener);
                  if (timeoutId !== null) clearTimeout(timeoutId);
                  if (checkClosed !== null) clearInterval(checkClosed);
                  options?.onSuccess?.();
                })
                .catch((error) => {
                  console.error(
                    `❌ Polling timeout for ${provider}:`,
                    error.message,
                  );
                  if (!handled) {
                    handled = true;
                    popup?.close();
                    window.removeEventListener("message", messageListener);
                    if (timeoutId !== null) clearTimeout(timeoutId);
                    if (checkClosed !== null) clearInterval(checkClosed);
                    options?.onError?.(error);
                  }
                });
            }

            // Message listener for popup communication
            const messageListener = (event: MessageEvent) => {
              console.log("📥 Received message:", {
                origin: event.origin,
                data: event.data,
              });

              // Verify origin
              const allowedOrigins = [
                window.location.origin,
                process.env.NODE_ENV === "development"
                  ? "http://localhost:4000"
                  : process.env.REACT_APP_API_URL ||
                    "https://api.sendupdates.ai",
              ];
              if (!allowedOrigins.includes(event.origin)) {
                console.log(
                  "❌ Message rejected - invalid origin:",
                  event.origin,
                );
                return;
              }

              if (
                event.data.type === "OAUTH_SUCCESS" &&
                event.data.provider === provider
              ) {
                console.log(`✅ OAuth completed successfully for ${provider}`);
                console.log(`🔔 Triggering onSuccess callback for ${provider}`);
                oauthCompleted = true;
                handled = true;
                popup?.close();
                window.removeEventListener("message", messageListener);
                if (timeoutId !== null) clearTimeout(timeoutId);
                if (checkClosed !== null) clearInterval(checkClosed);
                options?.onSuccess?.();
              } else if (
                event.data.type === "OAUTH_ERROR" &&
                event.data.provider === provider
              ) {
                console.log("❌ OAuth failed:", event.data.error);
                handled = true;
                popup?.close();
                window.removeEventListener("message", messageListener);
                if (timeoutId !== null) clearTimeout(timeoutId);
                if (checkClosed !== null) clearInterval(checkClosed);
                options?.onError?.(
                  new Error(event.data.error || "OAuth authentication failed"),
                );
              }
            };

            window.addEventListener("message", messageListener);

            // Fallback timeout (5 minutes)
            timeoutId = window.setTimeout(
              () => {
                if (!handled) {
                  console.warn("⏱️ OAuth popup timeout");
                  popup?.close();
                  window.removeEventListener("message", messageListener);
                  options?.onError?.(
                    new Error("OAuth authentication timed out"),
                  );
                }
              },
              5 * 60 * 1000,
            );

            // Poll to detect popup close
            let popupClosedTime: number | null = null;
            checkClosed = window.setInterval(() => {
              try {
                if (popup.closed) {
                  // For Close, give polling mechanism a grace period after popup closes
                  if (provider.toLowerCase() === "close") {
                    if (popupClosedTime === null) {
                      popupClosedTime = Date.now();
                      console.log(
                        `ℹ️ [${provider}] Popup closed, allowing 10s grace period for polling to complete...`,
                      );
                      return; // Keep checking
                    }

                    const gracePeriodMs = 10000; // 10 seconds
                    const elapsedSinceClose = Date.now() - popupClosedTime;

                    if (elapsedSinceClose < gracePeriodMs) {
                      // Still within grace period, keep checking
                      if (!oauthCompleted && !handled) {
                        // Log every 2 seconds
                        if (
                          Math.floor(elapsedSinceClose / 2000) >
                          Math.floor((elapsedSinceClose - 700) / 2000)
                        ) {
                          console.log(
                            `⏳ [${provider}] Grace period: ${((gracePeriodMs - elapsedSinceClose) / 1000).toFixed(1)}s remaining...`,
                          );
                        }
                        return; // Keep checking
                      } else {
                        // Connection detected during grace period
                        if (checkClosed !== null)
                          window.clearInterval(checkClosed);
                        return;
                      }
                    }

                    // Grace period expired
                    if (checkClosed !== null) window.clearInterval(checkClosed);
                    if (!oauthCompleted && !handled) {
                      console.log(
                        `⏱️ [${provider}] Grace period expired without connection`,
                      );
                      handled = true;
                      window.removeEventListener("message", messageListener);
                      if (timeoutId !== null) clearTimeout(timeoutId);
                      options?.onClose?.();
                    }
                    return;
                  }

                  // For non-Close providers (GitHub, Stripe), use original logic
                  if (checkClosed !== null) window.clearInterval(checkClosed);
                  if (!oauthCompleted && !handled) {
                    console.log(
                      "ℹ️ Popup closed, checking localStorage for result...",
                    );

                    // Check localStorage for fallback result (popup stores it before closing)
                    try {
                      const storedResult = localStorage.getItem(
                        "oauth_reauth_result",
                      );
                      if (storedResult) {
                        const result = JSON.parse(storedResult);
                        console.log("📦 Found stored OAuth result:", result);

                        // Check if it's recent (within last 10 seconds) and matches our provider
                        const isRecent = Date.now() - result.timestamp < 10000;
                        const isMatchingProvider =
                          result.provider?.toLowerCase() ===
                          provider.toLowerCase();

                        if (result.success && isRecent && isMatchingProvider) {
                          console.log(
                            "✅ OAuth completed successfully (via localStorage fallback)",
                          );
                          oauthCompleted = true;
                          handled = true;
                          localStorage.removeItem("oauth_reauth_result"); // Clean up
                          window.removeEventListener(
                            "message",
                            messageListener,
                          );
                          if (timeoutId !== null) clearTimeout(timeoutId);
                          options?.onSuccess?.();
                          return;
                        } else {
                          console.log("⚠️ Stored result doesn't match:", {
                            isRecent,
                            isMatchingProvider,
                            success: result.success,
                          });
                        }
                      } else {
                        console.log(
                          "ℹ️ No stored OAuth result found in localStorage",
                        );
                      }
                    } catch (storageError) {
                      console.error(
                        "❌ Error checking localStorage:",
                        storageError,
                      );
                    }

                    console.log("ℹ️ Popup closed without completing OAuth");
                    window.removeEventListener("message", messageListener);
                    if (timeoutId !== null) clearTimeout(timeoutId);
                    options?.onClose?.();
                  }
                  return;
                }
              } catch (e) {
                // Cross-origin - ignore until it returns to our origin
              }
            }, 700);
          } else {
            // For other providers, use redirect (existing behavior)
            window.location.href = response.data.url;
          }
        } else {
          throw new Error("Failed to get OAuth URL");
        }
      } else {
        // Non-OAuth providers route through Composio
        const composioResp = await apiService.post("/api/composio/connect", {
          provider,
          // Dedicated closer page — see pages/oauth/ComposioCallback.tsx.
          // The old /integrations target rendered a full app page inside the
          // popup and never closed it.
          callbackUrl: `${window.location.origin}/oauth/composio/callback?provider=${encodeURIComponent(provider)}`,
        });
        const redirectUrl = composioResp.data?.data?.redirectUrl || composioResp.data?.redirectUrl;
        if (!redirectUrl) {
          throw new Error(`Unable to connect ${provider}: no redirect URL received`);
        }
        const w = 500, h = 700;
        const left = window.screenX + (window.outerWidth - w) / 2;
        const top = window.screenY + (window.outerHeight - h) / 2;
        const popup = window.open(
          redirectUrl,
          `composio_connect_${provider}`,
          `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
        );
        // Poll for connection completion
        let attempts = 0;
        const pollInterval = setInterval(async () => {
          attempts++;
          try {
            const statusResp = await apiService.get(`/api/composio/connections/${provider}/status`);
            if (statusResp.data?.connected) {
              clearInterval(pollInterval);
              popup?.close();
              options?.onSuccess?.();
              return;
            }
          } catch { /* ignore polling errors */ }
          if (attempts >= 30) {
            clearInterval(pollInterval);
            popup?.close();
            options?.onSuccess?.();
          }
        }, 2000);
      }
    } catch (error) {
      console.error(`Failed to connect ${provider}:`, error);
      options?.onError?.(
        error instanceof Error ? error : new Error(String(error)),
      );
      throw error;
    }
  }

  /**
   * Re-authenticate an existing OAuth integration via popup (non-disruptive)
   */
  async reauthenticateIntegration(
    provider: string,
    settings?: any,
  ): Promise<void> {
    try {
      // Only OAuth providers use this flow
      const oauthProviders = this.getOAuthProviders();
      if (!oauthProviders.includes(provider)) {
        throw new Error(
          "Reauthentication is only supported for OAuth providers",
        );
      }

      // Build params and request the OAuth URL via authenticated XHR
      // Do NOT include popup/mode for XHR; those cause server redirect instead of JSON
      const queryParams: Record<string, string> = {};
      if (settings?.instance_url) {
        queryParams.instance_url = settings.instance_url;
      }

      let finalUrl: string | null = null;
      try {
        const response = await apiService.get(`/oauth/${provider}/url`, {
          params: queryParams,
        });
        if (response?.data?.url) {
          finalUrl = response.data.url as string;
        }
      } catch (xhrError) {
        console.warn(
          `XHR fetch of OAuth URL failed for ${provider}, falling back to direct navigation`,
          xhrError,
        );
      }

      if (!finalUrl) {
        throw new Error("Failed to get OAuth URL");
      }

      // Open a centered popup
      const width = 600;
      const height = 750;
      const left = Math.max(
        0,
        Math.floor(window.screenX + (window.outerWidth - width) / 2),
      );
      const top = Math.max(
        0,
        Math.floor(window.screenY + (window.outerHeight - height) / 2),
      );
      const popupFeatures = `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${width},height=${height},top=${top},left=${left}`;

      const popup = window.open(finalUrl, "oauth_reauth", popupFeatures);
      if (!popup) {
        // Fallback: navigate current window (last resort)
        window.location.href = finalUrl;
      } else {
        // Track if OAuth completed successfully
        let oauthCompleted = false;

        // Fallback auto-close: detect when popup returns to our origin and close it
        const poll = window.setInterval(() => {
          try {
            // Check if popup was closed by user without completing OAuth
            if (popup.closed) {
              window.clearInterval(poll);
              // Only send OAUTH_CLOSED if OAuth didn't complete successfully
              if (!oauthCompleted) {
                try {
                  window.postMessage(
                    { type: "OAUTH_CLOSED", provider, timestamp: Date.now() },
                    window.location.origin,
                  );
                } catch (e) {
                  // ignore
                }
              }
              return;
            }
            const href = popup.location?.href;
            if (href && href.startsWith(window.location.origin)) {
              // If redirected back to our app (e.g., /integrations?success=connected)
              if (
                href.includes("oauth_success=") ||
                href.includes("success=connected") ||
                href.includes("/integrations")
              ) {
                oauthCompleted = true;
                try {
                  window.postMessage(
                    { type: "OAUTH_SUCCESS", provider, timestamp: Date.now() },
                    window.location.origin,
                  );
                } catch (e) {
                  // ignore
                }
                popup.close();
                window.clearInterval(poll);
              }
            }
          } catch (e) {
            // Cross-origin - ignore until it returns to our origin
          }
        }, 700);
      }
    } catch (error) {
      console.error(`Failed to reauthenticate ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Connect API key based integrations
   */
  private async connectApiKeyIntegration(
    provider: string,
    settings: any,
  ): Promise<void> {
    try {
      const response = await apiService.post("/integrations/connect", {
        provider,
        auth_type: "api-key",
        settings,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to connect integration");
      }
    } catch (error) {
      console.error(
        `Failed to connect API key integration ${provider}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Disconnect an integration
   */
  async disconnectIntegration(provider: string): Promise<void> {
    try {
      const response = await apiService.delete(`/oauth/${provider}`);
      if (!response.data?.success) {
        const msg = response.data?.error || "Failed to disconnect integration";
        // Treat not-found/already-disconnected as success
        if (
          typeof msg === "string" &&
          msg.toLowerCase().includes("no active integration")
        ) {
          return;
        }
        throw new Error(msg);
      }
    } catch (error) {
      console.error(`Failed to disconnect ${provider}:`, error);
      // If server indicated not found, treat as success to keep UI consistent
      const anyErr: any = error as any;
      const data = anyErr?.response?.data;
      const msg: string | undefined = data?.error || anyErr?.message;
      if (msg && msg.toLowerCase().includes("no active integration")) {
        return;
      }
      throw error;
    }
  }

  /**
   * Test an integration connection
   */
  async testIntegration(provider: string): Promise<IntegrationTestResult> {
    try {
      const response = await apiService.get(`/integrations/${provider}/test`);
      return response.data.test_result;
    } catch (error) {
      // Surface server-provided error payload (Axios error on non-2xx)
      const anyErr: any = error as any;
      const serverData = anyErr?.response?.data;
      const testResult = serverData?.test_result as
        | IntegrationTestResult
        | undefined;

      if (testResult) {
        return testResult;
      }

      // Fallback: construct a minimal error result for the UI
      const message =
        serverData?.details ||
        serverData?.error ||
        anyErr?.message ||
        "Unknown error";
      return {
        provider,
        status: "error",
        response_time_ms: 0,
        has_data: false,
        error: typeof message === "string" ? message : JSON.stringify(message),
      };
    }
  }

  /**
   * Get integration health status for all connected providers
   */
  async getIntegrationHealth(): Promise<IntegrationHealthResult[]> {
    try {
      const response = await apiService.get("/integrations/health");
      return response.data.health_results || [];
    } catch (error) {
      console.error("Failed to get integration health:", error);
      throw error;
    }
  }

  /**
   * Get integration data for a specific provider
   */
  async getIntegrationData(
    provider: string,
    opts?: { start?: string; end?: string },
  ): Promise<any> {
    try {
      const response = await apiService.get(`/integrations/${provider}`, {
        params: {
          ...(opts?.start ? { start: opts.start } : {}),
          ...(opts?.end ? { end: opts.end } : {}),
        },
      });
      return response.data.data;
    } catch (error) {
      console.error(`Failed to get data for ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Fetch provider-specific selectable options (prod-safe)
   */
  async getProviderOptions(provider: string): Promise<
    | {
        type: "none";
        kinds?: Array<{ id: string; name: string; description?: string }>;
      }
    | {
        type: "list";
        label: string;
        items: Array<{ id: string; name: string }>;
        kinds?: Array<{ id: string; name: string; description?: string }>;
      }
  > {
    const maxRetries = 1;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await apiService.get(
          `/integrations/${provider}/options`,
        );
        if (response?.data?.success) return response.data.options;
        return { type: "none" } as const;
      } catch (err) {
        if (attempt < maxRetries) {
          await new Promise((r) => setTimeout(r, 1000));
          continue;
        }
        console.error(
          `Failed to fetch options for ${provider} after ${maxRetries + 1} attempts`,
        );
        return { type: "none" } as const;
      }
    }
    return { type: "none" } as const;
  }

  /**
   * Selection-aware fetch for provider
   */
  async fetchWithSelection(
    provider: string,
    payload: {
      selectedIds?: string[];
      kinds?: string[];
      start?: string;
      end?: string;
    },
  ): Promise<any> {
    const response = await apiService.post(
      `/integrations/${provider}/fetch`,
      payload,
    );
    return response.data?.data ?? response.data;
  }

  /**
   * Get integration data for multiple providers
   */
  async getMultipleIntegrationData(
    providers: string[],
    opts?: { start?: string; end?: string },
  ): Promise<Record<string, any>> {
    try {
      const response = await apiService.post("/integrations/multiple", {
        providers,
        ...(opts?.start ? { start: opts.start } : {}),
        ...(opts?.end ? { end: opts.end } : {}),
      });
      return response.data.data || {};
    } catch (error) {
      console.error("Failed to get multiple integration data:", error);
      throw error;
    }
  }

  /**
   * Get integration settings
   */
  async getIntegrationSettings(provider: string): Promise<any> {
    try {
      const response = await apiService.get(
        `/integrations/${provider}/settings`,
      );
      return response.data.settings || {};
    } catch (error) {
      console.error(`Failed to get settings for ${provider}:`, error);
      return {};
    }
  }

  /**
   * Update integration settings
   */
  async updateIntegrationSettings(
    provider: string,
    settings: any,
  ): Promise<void> {
    try {
      const response = await apiService.post(
        `/integrations/${provider}/settings`,
        {
          settings,
        },
      );

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to update settings");
      }
    } catch (error) {
      console.error(`Failed to update settings for ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Auto-join public Slack channels after OAuth connection
   */
  async autoJoinSlackChannels(): Promise<{
    success: boolean;
    joined: Array<{ id: string; name: string }>;
    errors: Array<{ channel: string; error: string }>;
    totalChannels: number;
    skipped: number;
  }> {
    try {
      const response = await apiService.post("/integrations/slack/auto-join");

      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to auto-join Slack channels",
        );
      }

      return {
        success: true,
        joined: response.data.joined || [],
        errors: response.data.errors || [],
        totalChannels: response.data.totalChannels || 0,
        skipped: response.data.skipped || 0,
      };
    } catch (error) {
      console.error("Failed to auto-join Slack channels:", error);
      // Return empty result instead of throwing to avoid blocking the user
      return {
        success: false,
        joined: [],
        errors: [
          {
            channel: "all",
            error: error instanceof Error ? error.message : "Unknown error",
          },
        ],
        totalChannels: 0,
        skipped: 0,
      };
    }
  }

  /**
   * Refresh integration data cache
   */
  async refreshIntegrationData(provider?: string): Promise<void> {
    try {
      const endpoint = provider
        ? `/integrations/${provider}/refresh`
        : "/integrations/refresh";

      const response = await apiService.post(endpoint);

      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to refresh integration data",
        );
      }
    } catch (error) {
      console.error("Failed to refresh integration data:", error);
      throw error;
    }
  }

  /**
   * Get OAuth callback URL for a provider
   */
  getOAuthCallbackUrl(provider: string): string {
    const baseUrl =
      process.env.REACT_APP_API_URL || "https://api.sendupdates.ai";
    return `${baseUrl}/oauth/${provider}/callback`;
  }

  /**
   * Handle OAuth callback (called after redirect from OAuth provider)
   */
  async handleOAuthCallback(
    provider: string,
    code: string,
    state?: string,
  ): Promise<void> {
    try {
      // Get any stored settings from session storage
      const storedSettings = sessionStorage.getItem(
        `integration_settings_${provider}`,
      );
      const settings = storedSettings ? JSON.parse(storedSettings) : null;

      const response = await apiService.post(`/oauth/${provider}/callback`, {
        code,
        state,
        settings,
      });

      // Clean up stored settings
      if (storedSettings) {
        sessionStorage.removeItem(`integration_settings_${provider}`);
      }

      if (!response.data.success) {
        throw new Error(response.data.error || "OAuth callback failed");
      }
    } catch (error) {
      console.error(`OAuth callback failed for ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Get supported integration providers
   */
  getSupportedProviders(): string[] {
    return [
      "github",
      "gitlab",
      "jira",
      "asana",
      "trello",
      "linear",
      "monday",
      "clickup",
      "stripe",
      "slack",
      "teams",
      "discord",
      "aws",
      "gcp",
      "azure",
      "heroku",
      "google-analytics",
      "google-sheets",
      "google-calendar",
      "google-drive",
      "gmail",
      "mixpanel",
      "datadog",
      "newrelic",
      "salesforce",
      "notion",
      "posthog",
      "amplitude",
      "mailchimp",
      "calendly",
      "airtable",
      "confluence",
      "figma",
      "hubspot",
      "attio",
      "heap",
      "segment",
      "marketo",
    ];
  }

  /**
   * Get OAuth providers (vs API key providers)
   */
  getOAuthProviders(): string[] {
    return [
      "github",
      "gitlab",
      "jira",
      "asana",
      "trello",
      "linear",
      "monday",
      "clickup",
      "stripe",
      "slack",
      "teams",
      "discord",
      "gcp",
      "azure",
      "heroku",
      "google-analytics",
      "google-sheets",
      "google-calendar",
      "google-drive",
      "gmail",
      "salesforce",
      "notion",
      "posthog",
      "amplitude",
      "zendesk",
      "intercom",
      "pipedrive",
      "mailchimp",
      "close",
      "calendly",
      "airtable",
      "confluence",
      "figma",
      "hubspot",
      "attio",
      "heap",
      "segment",
      "trello",
      "marketo",
    ];
  }

  /**
   * Get API key providers
   */
  getApiKeyProviders(): string[] {
    return ["aws", "mixpanel", "datadog", "newrelic", "beehiiv", "odoo", "marketo"];
  }

  /**
   * Validate integration settings
   */
  validateIntegrationSettings(
    provider: string,
    settings: any,
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    switch (provider) {
      case "aws":
        if (!settings?.access_key_id)
          errors.push("AWS Access Key ID is required");
        if (!settings?.secret_access_key)
          errors.push("AWS Secret Access Key is required");
        if (!settings?.region) errors.push("AWS Region is required");
        break;

      case "mixpanel":
        if (!settings?.api_secret)
          errors.push("Mixpanel API Secret is required");
        if (!settings?.project_id)
          errors.push("Mixpanel Project ID is required");
        break;

      case "datadog":
        if (!settings?.api_key) errors.push("Datadog API Key is required");
        if (!settings?.app_key)
          errors.push("Datadog Application Key is required");
        break;

      case "newrelic":
        if (!settings?.api_key) errors.push("New Relic API Key is required");
        if (!settings?.account_id)
          errors.push("New Relic Account ID is required");
        break;

      case "beehiiv":
        if (!settings?.api_key) errors.push("Beehiiv API Key is required");
        if (!settings?.publication_id)
          errors.push("Beehiiv Publication ID is required");
        break;

      case "odoo":
        if (!settings?.api_key) errors.push("Odoo API Key is required");
        if (!settings?.instance_url)
          errors.push("Odoo Instance URL is required (e.g., https://mycompany.odoo.com)");
        if (!settings?.database)
          errors.push("Odoo Database name is required");
        break;

      case "marketo":
        if (!settings?.client_id) errors.push("Marketo Client ID is required");
        if (!settings?.client_secret)
          errors.push("Marketo Client Secret is required");
        if (!settings?.subdomain)
          errors.push("Marketo Subdomain is required (e.g., 123-ABC-456)");
        break;
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Poll the backend API to detect when Close integration connection is established
   * This is needed because OAuth redirects break window.opener, preventing postMessage
   *
   * @param provider - Provider name (should be "close")
   * @param maxDurationMs - Maximum time to poll (default 180s)
   * @param startIntervalMs - Initial polling interval (default 1000ms)
   * @returns Promise that resolves when connection is detected, rejects on timeout
   */
  private async startCloseConnectionPolling(
    provider: string,
    maxDurationMs = 180000, // 3 minutes
    startIntervalMs = 1000, // 1 second
  ): Promise<void> {
    const startTime = Date.now();
    const backoffMultiplier = 1.5;
    let currentInterval = startIntervalMs;
    let attemptCount = 0;

    console.log(`🔄 [POLLING] Starting connection polling for ${provider}...`);
    console.log(
      `🔄 [POLLING] Max duration: ${maxDurationMs / 1000}s, Initial interval: ${startIntervalMs}ms`,
    );

    return new Promise((resolve, reject) => {
      const checkConnection = async () => {
        attemptCount++;
        const elapsedMs = Date.now() - startTime;
        const elapsedSec = (elapsedMs / 1000).toFixed(1);

        // Check if we've exceeded max duration
        if (elapsedMs > maxDurationMs) {
          console.error(
            `❌ [POLLING] Timeout after ${attemptCount} attempts (${elapsedSec}s)`,
          );
          reject(
            new Error(
              `Connection polling timeout - ${provider} not detected after ${elapsedSec}s`,
            ),
          );
          return;
        }

        console.log(
          `🔍 [POLLING] Attempt ${attemptCount} - checking for ${provider} connection... (${elapsedSec}s elapsed)`,
        );

        try {
          // Fast DB-only check — the Nango webhook should have already created the record
          const integrations = await this.getUserIntegrations();
          const activeProviders = integrations
            .filter((int: any) => int?.integration_status === "active")
            .map((int: any) => int.provider?.toLowerCase());

          // Check if our provider is now connected
          if (
            activeProviders.some(
              (p: string) => p?.toLowerCase() === provider.toLowerCase(),
            )
          ) {
            console.log(
              `✅ [POLLING] ${provider} connection detected after ${attemptCount} attempts (${elapsedSec}s)`,
            );
            resolve();
            return;
          }

          console.log(
            `⏳ [POLLING] ${provider} not found yet, retrying in ${currentInterval}ms...`,
          );

          // Schedule next check with exponential backoff
          setTimeout(checkConnection, currentInterval);
          currentInterval = Math.min(
            currentInterval * backoffMultiplier,
            10000, // Cap at 10 seconds
          );
        } catch (error) {
          // Don't fail on network errors, just log and retry
          console.warn(
            `⚠️ [POLLING] API error on attempt ${attemptCount}, will retry:`,
            error,
          );
          setTimeout(checkConnection, currentInterval);
        }
      };

      // Start the first check immediately
      checkConnection();
    });
  }
}

export const integrationsService = new IntegrationsService();
export default integrationsService;
